import { memo, useCallback, useState } from 'react';
import { Badge, Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ShieldAlert, ShieldCheck } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

/**
 * MFA of the member: Enabled with the method and enrollment date, or Not enabled with the
 * reminder button (only for an active member; the reminder can be sent again).
 *
 * @param {object} props
 * @param {{ enabled: boolean, method: string|null, enrolledAt: string|null }} props.mfa
 * @param {string} props.locale
 * @param {boolean} props.canRemind The member is active, so a reminder makes sense.
 * @param {() => Promise<boolean>} props.onRemind
 */
function MemberMfaCard({ mfa, locale, canRemind, onRemind }) {
  const { t } = useTranslation('people-access');
  const [sending, setSending] = useState(false);

  const handleRemind = useCallback(async () => {
    setSending(true);
    try {
      await onRemind();
    } finally {
      setSending(false);
    }
  }, [onRemind]);

  const Icon = mfa.enabled ? ShieldCheck : ShieldAlert;
  const enrolled = mfa.enrolledAt ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(mfa.enrolledAt)) : '';

  return (
    <Card className="flex flex-col" data-testid="member-mfa-card">
      <div className="flex items-center justify-between gap-3 border-b border-edge px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <Icon className="h-4 w-4 text-content-muted" aria-hidden="true" />
          {t('memberTabs.security.mfa.title')}
        </h3>
        <Badge variant={mfa.enabled ? 'success' : 'warning'}>
          {mfa.enabled ? t('memberTabs.security.mfa.enabled') : t('memberTabs.security.mfa.notEnabled')}
        </Badge>
      </div>
      {mfa.enabled ? (
        <dl className="grid grid-cols-2 gap-4 px-4 py-3 text-sm">
          <div className="flex flex-col gap-0.5">
            <dt className="text-xs uppercase text-content-muted">{t('memberTabs.security.mfa.method')}</dt>
            <dd className="text-content-secondary">{t(`memberTabs.security.mfa.methods.${mfa.method}`)}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-xs uppercase text-content-muted">{t('memberTabs.security.mfa.enrolled')}</dt>
            <dd className="text-content-secondary">{enrolled}</dd>
          </div>
        </dl>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex flex-col gap-0.5 text-sm">
            <span className="text-content-secondary">{t('memberTabs.security.mfa.notEnrolled')}</span>
            <span className="text-xs text-content-muted">
              {canRemind ? t('memberTabs.security.mfa.remindHint') : t('memberTabs.security.mfa.notActive')}
            </span>
          </div>
          {canRemind ? (
            <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
              <Button variant="secondary" size="sm" loading={sending} onClick={handleRemind}>
                {t('memberTabs.security.mfa.sendReminder')}
              </Button>
            </Can>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export default memo(MemberMfaCard);
